import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
} from '@mui/material'
import uuid from 'ply-utils/uuid'
import * as React from 'react'
import { createWithEqualityFn as create } from 'zustand/traditional'

interface Prompt {
	id: string | number
	Dialog: React.ElementType
	open: boolean
}

interface State {
	prompts: Prompt[]
	open(prompt: Prompt): void
	close(id: Prompt['id']): void
	destroy(id: Prompt['id']): void
}

type resolve = (result: unknown) => void
type reject = (error: unknown) => void
type PromptCallback = (
	resolve: resolve,
	reject: reject,
) => {
	title: React.ReactNode
	content: React.ReactNode
	actions?: React.ReactNode
}

type promptHook = (callback: PromptCallback) => Promise<unknown>

const useStore = create<State>()((set) => ({
	prompts: [],

	open(prompt: Prompt) {
		set((state) => ({
			...state,
			prompts: [...state.prompts, prompt],
		}))
	},

	close(id: Prompt['id']) {
		set((state) => ({
			...state,
			prompts: state.prompts.map<Prompt>((p) =>
				p.id === id ? { ...p, open: false } : p,
			),
		}))
	},

	destroy(id: Prompt['id']) {
		set((state) => ({
			...state,
			prompts: state.prompts.filter((p) => p.id !== id),
		}))
	},
}))

const PromptContext = React.createContext<promptHook | null>(null)

PromptContext.displayName = 'PromptContext'

export const usePrompt = () => {
	const value = React.useContext(PromptContext)

	if (!value) {
		throw Error('usePrompt should be used in PromptProvider')
	}

	return value
}

const { close } = useStore.getState()

export function PromptProvider(props: any) {
	const prompts = useStore((s) => s.prompts)

	function prompt(callback: PromptCallback) {
		const id = uuid()

		return new Promise((resolve, reject) => {
			const { title, content, actions } = callback(resolve, reject)
			const { open, destroy } = useStore.getState()

			open({
				id,
				open: true,
				Dialog: ({ open }: { open: boolean }) => {
					return (
						<Dialog
							open={open}
							onClose={() => reject()}
							aria-labelledby="alert-dialog-title"
							aria-describedby="alert-dialog-description"
							slotProps={{
								transition: {
									onExited: () => destroy(id),
								},
							}}
						>
							<DialogTitle id="alert-dialog-title">{title}</DialogTitle>
							<DialogContent>
								<DialogContentText id="alert-dialog-description">
									{content}
								</DialogContentText>
							</DialogContent>
							<DialogActions>
								{actions ?? (
									<>
										<Button onClick={() => reject()} color="primary">
											Cancel
										</Button>
										<Button
											onClick={() => {
												resolve(null)
											}}
											color="primary"
											autoFocus
										>
											OK
										</Button>
									</>
								)}
							</DialogActions>
						</Dialog>
					)
				},
			})
		}).finally(() => close(id))
	}

	return (
		<>
			<PromptContext.Provider
				{...props}
				value={React.useCallback(prompt, [])}
			/>
			{prompts.map(({ id, Dialog, open }) => (
				<Dialog key={id} open={open} />
			))}
		</>
	)
}
